import { ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { EquipmentExtendedClient } from '@frontend/equipment/api';
import { EquipmentExtended, EquipmentExtendedListResponse } from '@frontend/equipment/types';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { ProductClient } from '@frontend/product/api';
import { IProduct } from '@frontend/product/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAuditTransaction } from '../../../hooks';
import { useAppSelector } from '../../../redux/store';
import { useKioskNavigate } from '../../../utils/kiosk-navigation';

export const useEquipmentList = () => {
    const navigate = useKioskNavigate();
    const intl = useIntl();
    const { productId, slotId } = useParams();
    const navigationState = useAppSelector((store) => store.navigation);
    const spot = navigationState.spot;
    const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
    const [product, setProduct] = useState<IProduct | null>(null);
    const [slot, setSlot] = useState<Slot | null>(null);
    const [selectedEquipment, setSelectedEquipment] = useState<EquipmentExtended | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchValue, setSearchValue] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('search');

    const { isSubmitting, error, setError, createTransactionWithPackage } = useAuditTransaction({
        id: 'EQUIPMENT_LIST',
        application: Application.SPOTS,
        feature: Feature.LAYOUT,
        slot,
        onComplete: () => navigate('../../', { replace: true, state: { fromEquipmentAddFlow: true } })
    });

    useEffect(() => {
        if (!productId || !accountId) {
            setIsLoading(false);
            return;
        }
        ProductClient.fetchProduct(accountId, productId)
            .then((product) => {
                setProduct(product);
            })
            .catch(() => {
                setError(CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.PRODUCT.DEFAULT));
                setProduct(null);
            })
            .finally(() => setIsLoading(false));
    }, [productId, accountId, setError]);

    useEffect(() => {
        if (!slotId || !spot || !accountId) {
            setIsLoading(false);
            if (!slotId) setError(CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.SLOT.DEFAULT));
            return;
        }
        SlotClient.fetchAccountSpotSlot(accountId, spot.id, slotId)
            .then((slot) => {
                setSlot(slot);
            })
            .catch(() => {
                setError(CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.SLOT.DEFAULT));
                setSlot(null);
            })
            .finally(() => setIsLoading(false));
    }, [slotId, spot, accountId, setError]);

    const fetch = useCallback(
        (arg?: ApiQueryParams<DefaultQueryParams>): Promise<EquipmentExtendedListResponse> =>
            EquipmentExtendedClient.fetchEquipmentsExtended({
                ...arg,
                product_id: productId,
                [searchKey]: searchValue || undefined
            }),
        [productId, searchValue, searchKey]
    );

    const handleSearchChange = useCallback((value: string, filterKey: string | null) => {
        setSearchValue(value);
        if (filterKey) setSearchKey(filterKey);
    }, []);

    const handleEquipmentAdd = () => {
        if (!selectedEquipment) return;
        createTransactionWithPackage({
            accountId: selectedEquipment.account_id,
            productId: selectedEquipment.product_id,
            equipmentId: selectedEquipment.id
        });
    };

    return {
        navigate,
        isLoading,
        isSubmitting,
        error,
        hasError: Boolean(error),
        fetch,
        selectedEquipment,
        setSelectedEquipment,
        product,
        handleEquipmentAdd,
        searchValue,
        searchKey,
        handleSearchChange,
        intl
    };
};
